import {
  Component,
  ElementRef,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { Doctor } from 'src/app/entities/user.entity/doctor.entity';
import { AuthService } from 'src/app/services/auth.service/auth.service';
import { Bordearaux } from 'src/app/entities/bordearaux.entity/bordearaux';
import { MontantLettrePipe } from '../../pipes/montantLettre.pipe/montant-lettre.pipe';
import { EUserRole } from 'src/app/entities/user.entity';
import { DoctorSecretary } from 'src/app/entities/user.entity/doctor.entity/secretary.entity';

@Component({
  selector: 'app-payant',
  templateUrl: './payant.component.html',
  styleUrls: ['./payant.component.css'],
})
export class PayantComponent implements OnInit, OnChanges, OnDestroy {
  // ─── Inputs ──────────────────────────────────────────────────────────────────
  @Input() bordearaux: Array<Bordearaux> = [];
  @Input() startDate!: Date;
  @Input() finDate!: Date;

  // ─── State ───────────────────────────────────────────────────────────────────
  /** Toutes les lignes affichées à l'écran (cochées, décochées, contrôles) */
  displayedRows: any[] = [];
  /** Lignes retenues : total, impression, fichier CNAM */
  visibleBordereaux: any[] = [];
  paginatedData: any[] = [];

  total: number = 0;
  pageSize = 10;
  currentPage = 1;
  totalPages = 0;
  today: string = '';
nbExclues: number = 0;
  /** Groupement des consultations par patient (triées par date) */
  consultationsParPatient: Record<string, any[]> = {};

  private readonly DELAI_CONTROLE_JOURS = 15;
  private readonly MS_PAR_JOUR = 24 * 60 * 60 * 1000;

  private _lastBordearauxRef: any[] | null = null;
  private _isPrinting = false;

  @ViewChild('printSection', { static: false }) printSection!: ElementRef;

  constructor(
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
  ) {}

  // ─── Médecin courant ─────────────────────────────────────────────────────────

  get doctor(): Doctor {
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) return new Doctor({} as any);

    if (currentUser.role === EUserRole.DOCTOR_SECRETARY) {
      const secretary = currentUser as DoctorSecretary;
      if (secretary.doctor) {
        return new Doctor(secretary.doctor);
      }
    }
    return new Doctor(currentUser);
  }

  getDisplayName(doctor: any): string {
    return doctor?.role === 'Secretary'
      ? doctor.doctor.getFullName()
      : doctor.getFullName();
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────────

  ngOnInit(): void {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    this.today = `${year}${month}${day}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['bordearaux'] && this.bordearaux) {
      if (this.bordearaux !== this._lastBordearauxRef) {
        this.bordearaux = this.bordearaux.map((item: any) => ({
          ...item,
          isDisabled:
            typeof item.isDisabled === 'boolean' ? item.isDisabled : true,
          apcis: item.apcis
            ? String(item.apcis)
                .split(',')
                .map((val: string) => String(Number(val.trim()) + 1))
                .join(',')
            : (item.apcis ?? ''),
        }));

        // ➜ Règle des 15 jours (consultation de contrôle)
        this.appliquerRegleControle(this.bordearaux);

        this._lastBordearauxRef = this.bordearaux;
      }

      this.currentPage = 1;
      this.updateVisibleRows();
    
    }

    if ((changes['startDate'] || changes['finDate']) && !changes['bordearaux']) {
      this.currentPage = 1;
      this.updatePaginatedData();
    }
  }

  ngOnDestroy(): void {
    this._isPrinting = false;
    this._lastBordearauxRef = null;
  }

  // ─── Règle des 15 jours ──────────────────────────────────────────────────────

  /** Convertit n'importe quel format de date en index de jour (minuit local) */
  private toDayIndex(value: any): number | null {
    if (value == null || value === '') return null;
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return null;
    const minuit = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    return Math.round(minuit / this.MS_PAR_JOUR);
  }

  /** Clé de regroupement : patientid, sinon I.U + nom en secours */
  private getPatientKey(row: any): string {
    if (row.patientid) return String(row.patientid);
    return [row.cnamCode ?? '', row.firstName ?? '', row.lastName ?? '', row.birthDate ?? '']
      .join('|')
      .toLowerCase()
      .trim();
  }

  private isConsultation(row: any): boolean {
    const motif = String(row.motif ?? '').trim().toUpperCase();
    return motif === 'CS' || motif === 'C1';
  }

  /**
   * Groupe par patient, trie par date, et marque comme "contrôle"
   * toute consultation < 15 jours après la dernière consultation facturée.
   * Les objets sont modifiés en place (l'ordre d'affichage est conservé).
   */
  appliquerRegleControle(rows: any[]): any[] {
    // 1. Groupement par patient
    this.consultationsParPatient = rows.reduce((acc, row) => {
      const key = this.getPatientKey(row);
      (acc[key] ??= []).push(row);
      return acc;
    }, {} as Record<string, any[]>);

    let nbControles = 0;

    // 2. Pour chaque patient : tri par date puis comparaison
    Object.values(this.consultationsParPatient).forEach((lignes) => {
      lignes.sort(
        (a, b) => (this.toDayIndex(a.date) ?? 0) - (this.toDayIndex(b.date) ?? 0),
      );

      let referenceJour: number | null = null; // dernière consultation facturée

      for (const ligne of lignes) {
        ligne.isControle = false;
        ligne.joursDepuis = null;

        if (!this.isConsultation(ligne)) continue; // actes ignorés

        const jour = this.toDayIndex(ligne.date);
        if (jour === null) continue; // date invalide → on ne touche pas

        const ecart = referenceJour === null ? Infinity : jour - referenceJour;

        if (ecart < this.DELAI_CONTROLE_JOURS) {
          ligne.isControle = true;
          ligne.joursDepuis = ecart;
          ligne.isDisabled = false; // décochée → exclue
          nbControles++;
        } else {
          referenceJour = jour; // nouvelle consultation facturée
        }
      }
    });

  

    return rows;
  }

  // ─── Visibilité & pagination ─────────────────────────────────────────────────

updateVisibleRows(): void {
  // Coché par l'utilisateur sur une ligne contrôle → elle redevient facturable
  this.bordearaux.forEach((b: any) => {
    if (b.isDisabled === true && b.isControle) {
      b.isControle = false;
      b.controleLeve = true;
    } else if (b.isDisabled === false && b.controleLeve) {
      // Décochée à nouveau → on restaure le statut contrôle
      b.isControle = true;
      b.controleLeve = false;
    }
  });

  this.displayedRows = this.bordearaux;

  // ⚠️ Ce filtre doit être fait AVANT calculateTotal()
  this.visibleBordereaux = this.bordearaux.filter((b: any) => this.estFacturable(b));
  this.nbExclues = this.bordearaux.length - this.visibleBordereaux.length;

  this.total = this.calculateTotal();
  this._refreshPagination();
}

estFacturable(bord: any): boolean {
  return bord.isDisabled === true && !bord.isControle;
}

private calculateTotal(): number {
  const somme = this.visibleBordereaux.reduce(
    (sum, bord) => sum + this.montantCnam(bord),
    0,
  );
  return Math.round(somme * 1000) / 1000;
}

  private _refreshPagination(): void {
    this.totalPages = Math.ceil(this.displayedRows.length / this.pageSize);

    if (this.currentPage > this.totalPages && this.totalPages > 0) {
      this.currentPage = this.totalPages;
    }
    if (this.totalPages === 0) {
      this.currentPage = 1;
    }
    this.updatePaginatedData();
  }

  updatePaginatedData(): void {
    const start = (this.currentPage - 1) * this.pageSize;
    this.paginatedData = this.displayedRows.slice(start, start + this.pageSize);
  }

  // ─── Calcul du total ─────────────────────────────────────────────────────────

  private montantCnam(bord: any): number {
    return bord.apcis !== ''
      ? bord.regularConsultationAmountCNAM
      : bord.regularConsultationAmountCNAM - (bord.regularConsultationAmountCNAMTP || 0);
  }



  // ─── Navigation ──────────────────────────────────────────────────────────────

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.updatePaginatedData();
  }

  nextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  prevPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  getQuality(qualityCode: number): string {
    const qualities: { [key: number]: string } = {
      0: 'Assuré',
      1: 'Conjoint',
      2: 'Mère',
      3: 'Père',
      4: 'Enfant',
    };
    return qualities[qualityCode] || 'N/A';
  }

  formatDateToYYYYMMDD(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  }

  formatDateToDDMMYYYY(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }

  formatApcis(apcis: number | string): string {
    if (apcis == null || apcis === '') return '0000';
    const normalized = String(apcis).replace(',', '.');
    const num = Number(normalized);
    if (isNaN(num)) return '0000';
    if (Number.isInteger(num)) return String(num).padStart(4, '0');
    return String(Math.floor(num)).padStart(4, '0');
  }

  // ─── Impression ──────────────────────────────────────────────────────────────

  async print(): Promise<void> {
    if (this._isPrinting) return;
    this._isPrinting = true;
    try {
      this.printDirectly();
    } finally {
      setTimeout(() => {
        this._isPrinting = false;
      }, 1500);
    }
  }

  generatePrintHTML(): string {
    if (!this.printSection?.nativeElement) return '';

    const table = this.printSection.nativeElement.querySelector('table');
    if (!table) return '';

    const theadRow = table.querySelector('thead tr');
    const colCount = theadRow ? theadRow.children.length + 1 : 12;
    const colPct = (100 / colCount).toFixed(4);

    const cleanColgroupHTML = `<colgroup>${Array.from({ length: colCount })
      .map(() => `<col style="width:${colPct}%">`)
      .join('')}</colgroup>`;

    const theadClone = table.querySelector('thead').cloneNode(true) as HTMLElement;
    theadClone.querySelectorAll('th, td').forEach((cell: Element) => {
      (cell as HTMLElement).removeAttribute('width');
      (cell as HTMLElement).style.removeProperty('width');
      (cell as HTMLElement).style.removeProperty('min-width');
      (cell as HTMLElement).style.removeProperty('max-width');
    });

    const theadRows = theadClone.querySelectorAll('tr');
    if (theadRows.length > 0) {
      const th = document.createElement('th');
      th.textContent = 'N°';
      th.style.cssText = 'text-align:center;width:40px;';
      if (theadRows.length > 1) th.setAttribute('rowspan', String(theadRows.length));
      theadRows[0].insertBefore(th, theadRows[0].firstChild);
    }

    const theadHTML = theadClone.outerHTML;
    const total = this.calculateTotal();
    const totalEnLettre = new MontantLettrePipe().transform(total);
    const totalPrintPages = Math.ceil(this.visibleBordereaux.length / this.pageSize);

    let printHTML = '';

    for (let page = 0; page < totalPrintPages; page++) {
      const start = page * this.pageSize;
      const pageData = this.visibleBordereaux.slice(start, start + this.pageSize);
      const isLastPage = page === totalPrintPages - 1;

      let tbodyHTML = '<tbody>';
      pageData.forEach((bord, index) => {
        const tpAmount = bord.apcis !== '' ? 0 : bord.regularConsultationAmountCNAMTP || 0;
        const cnamToPay = this.montantCnam(bord);

        tbodyHTML += `
          <tr class="row3">
            <td style="text-align:center;">${start + index + 1}</td>
            <td>${this.formatDateToDDMMYYYY(new Date(bord.date))}</td>
            <td>${bord.cnamCode ?? ''}</td>
            <td>${bord.firstName} ${bord.lastName}</td>
            <td>${this.getQuality(bord.cnamAssociation)}</td>
            <td>${bord.apcis ?? ''}</td>
            <td>${bord.motif}</td>
            <td>${bord.descActe ?? ''}</td>
            <td>${bord.regularConsultationAmountCNAM}</td>
            <td>${tpAmount}</td>
            <td>${cnamToPay}</td>
            <td>${this.doctor.cnam}</td>
          </tr>`;
      });
      tbodyHTML += '</tbody>';

      printHTML += `
        <div class="page-block" style="width:100%;display:block;">
          <table style="width:100% !important;table-layout:fixed !important;border-collapse:collapse;">
            ${cleanColgroupHTML}
            ${theadHTML}
            ${tbodyHTML}
          </table>
          ${
            isLastPage
              ? `
          <table style="width:100% !important;table-layout:fixed !important;border-collapse:collapse;font-size:10px;margin-bottom:15px;">
            <tr>
              <td colspan="9" style="border:1px solid #000;padding:4px 8px;font-weight:bold;text-transform:uppercase;">TOTAL À PAYER</td>
              <td colspan="2" style="border:1px solid #000;padding:4px 8px;font-weight:bold;text-align:center;">${total.toFixed(3)} TND</td>
            </tr>
          </table>
          <strong style="margin-top:15px">Arrêté le présent décompte à la somme de :</strong> ${totalEnLettre ?? ''}
          `
              : ''
          }
        </div>
      `;

      if (!isLastPage) printHTML += `<div class="page-break"></div>`;
    }

    return printHTML;
  }

  printDirectly(): void {
    if (!this.printSection?.nativeElement) return;

    const totalPrintPages = Math.ceil(this.visibleBordereaux.length / this.pageSize);
    const contentToPrint = this.generatePrintHTML();

    const printWindow = window.open(
      '',
      '_blank',
      'width=1123,height=794,menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes',
    );
    if (!printWindow) {
      this._isPrinting = false;
      return;
    }

    printWindow.document.open();
    printWindow.document.write(`<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <title>Impression - Note d'Honoraire</title>
  <style>
    @media print {
      @page {
        size: A4 landscape;
        margin: 8mm 5mm 18mm 5mm;
        @bottom-center {
          content: "Page " counter(page) " / ${totalPrintPages}";
          font-family: Calibri, Arial, sans-serif;
          font-size: 8pt;
          color: #333;
          border-top: 1px solid #999;
          padding-top: 3px;
        }
      }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; margin: 0 !important; padding: 0 !important; }
      .print-container, .page-block { width: 100% !important; display: block !important; }
      table { width: 100% !important; table-layout: auto !important; border-collapse: collapse !important; }
      col { width: auto !important; }
      th, td { border: 1px solid #000 !important; padding: 3px !important; text-align: center !important; word-wrap: break-word !important; font-size: 8pt !important; }
      thead { display: table-header-group; }
      .page-break { page-break-after: always; break-after: page; }
      .no-print, .class-row { display: none !important; }
    }
    body { font-family: Calibri, Arial, sans-serif; font-size: 9pt; margin: 5mm; }
    table { width: 100% !important; border-collapse: collapse; }
    th, td { border: 1px solid #000; padding: 3px; text-align: center; word-wrap: break-word; font-size: 8pt; }
    .page-break { page-break-after: always; break-after: page; }
  </style>
</head>
<body>
  <div class="print-container">${contentToPrint}</div>
  <script>
    window.onload = function() {
      try {
        document.querySelectorAll('table').forEach(function(t) {
          t.removeAttribute('width');
          t.style.setProperty('width','100%','important');
          t.style.setProperty('table-layout','auto','important');
        });
        document.querySelectorAll('col').forEach(function(c) {
          c.removeAttribute('width');
          c.style.setProperty('width','auto','important');
        });
        document.querySelectorAll('th, td').forEach(function(c) {
          c.removeAttribute('width');
          c.style.removeProperty('width');
        });
        window.focus();
        window.print();
        window.onafterprint = function() { window.close(); };
      } catch(e) {
        document.body.innerHTML += '<div style="color:red;padding:20px">Erreur impression: ' + e.message + '</div>';
      }
    };
  </script>
</body>
</html>`);
    printWindow.document.close();
  }

  // ─── Téléchargement fichier CNAM ─────────────────────────────────────────────

  downloadTextFile(): void {
    const totalMillimes = Math.round(this.total * 1000);

    let text =
      '1' +
      this.doctor.cnam.padStart(12, '0') +
      this.today +
      this.formatDateToYYYYMMDD(this.startDate) +
      this.formatDateToYYYYMMDD(this.finDate) +
      this.formatDateToYYYYMMDD(this.startDate) +
      this.doctor.cnam.padStart(12, '0') +
      String(this.visibleBordereaux.length).padStart(5, '0') +
      String(totalMillimes).padStart(10, '0') +
      '0000000000' +
      String(totalMillimes).padStart(10, '0');

    this.visibleBordereaux.forEach((bord, index) => {
      const rowOrder = String(index + 1).padStart(5, '0');
      const regularAmount = Math.round(bord.regularConsultationAmountCNAM * 1000);
      const tpAmount =
        bord.apcis !== '' ? 0 : Math.round((bord.regularConsultationAmountCNAMTP || 0) * 1000);
      const cnamToPay = bord.apcis !== '' ? regularAmount : regularAmount - tpAmount;
      const matriculeAssure = bord.cnamCode != null ? bord.cnamCode : 0;

      const line =
        '\n2' +
        rowOrder +
        String(bord.affiliationCode) +
        String(matriculeAssure).padStart(10, '0') +
        bord.cnamAssociation +
        String(bord.childRank).padEnd(2, '0') +
        (bord.firstName + ' ' + bord.lastName).padEnd(60, ' ') +
        this.formatDateToYYYYMMDD(new Date(bord.birthDate)) +
        this.doctor.cnam.padStart(12, '0') +
        this.formatDateToYYYYMMDD(new Date(bord.date)) +
        String(regularAmount).padStart(10, '0') +
        String(regularAmount).padStart(10, '0') +
        String(tpAmount).padStart(10, '0') +
        String(cnamToPay).padStart(10, '0') +
        this.formatApcis(bord.apcis);

      if (bord.motif !== 'C1' && bord.motif !== 'CS') {
        const line2 =
          '\n3' +
          rowOrder +
          String(bord.motif).padEnd(10, ' ') +
          String(Math.round(bord.montantConsultationActe * 1000)).padStart(10, '0') +
          String(
            bord.apcis !== ''
              ? ''.padStart(10, '0')
              : Math.round((bord.regularConsultationAmountCNAMTP || 0) * 1000),
          ).padStart(10, '0') +
          String(bord.apcis.length > 0 ? 'O' : 'N') +
          String(bord.pec ?? '').padStart(12, '0');

        text += line + line2;
      } else {
        text += line;
      }
    });

    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download =
      'Bordereau ' +
      this.formatDateToDDMMYYYY(this.startDate) +
      ' au ' +
      this.formatDateToDDMMYYYY(this.finDate) +
      '.txt';

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    setTimeout(() => URL.revokeObjectURL(url), 500);
  }
}